import { useCallback, useEffect, useRef, useState } from 'react'

interface State<T> {
  data: T | null
  error: unknown
  loading: boolean
}

/**
 * Fetch on mount and then on an interval.
 *
 * <p>Polling pauses while the tab is hidden. A monitoring dashboard is exactly
 * the kind of page that gets left open on a spare screen for weeks, and there
 * is no point querying every ten seconds for something nobody is looking at.
 */
export function usePolling<T>(
  fetcher: () => Promise<T>,
  intervalMs: number,
  deps: unknown[] = [],
): State<T> & { refresh: () => void } {
  const [state, setState] = useState<State<T>>({ data: null, error: null, loading: true })
  const mounted = useRef(true)
  // Keep the newest fetcher without making the effect re-subscribe on every
  // render, which would restart the timer continuously.
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher

  const load = useCallback(async () => {
    try {
      const data = await fetcherRef.current()
      if (mounted.current) setState({ data, error: null, loading: false })
    } catch (error) {
      if (mounted.current) {
        setState((prev) => ({ data: prev.data, error, loading: false }))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    mounted.current = true
    setState((prev) => ({ ...prev, loading: prev.data === null }))
    load()

    let timer: number | undefined
    const start = () => {
      stop()
      timer = window.setInterval(() => {
        if (document.visibilityState === 'visible') load()
      }, intervalMs)
    }
    const stop = () => {
      if (timer !== undefined) window.clearInterval(timer)
      timer = undefined
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') load()
    }

    start()
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      mounted.current = false
      stop()
      document.removeEventListener('visibilitychange', onVisibility)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, ...deps])

  return { ...state, refresh: load }
}
